import { getListForPage, DEFAULT_AMOUNT, type PageKey } from "@/lib/lenderSelection";
import type { Lender } from "@/lib/lenders";

/** Plockar ut högsta talet ur en räntesträng, t.ex. "7,90% - 21,90%" -> 21.9 */
function maxRate(rate: string): number {
  const nums = (rate.match(/\d+[,.]?\d*/g) ?? []).map((n) => Number(n.replace(",", ".")));
  return nums.length ? Math.max(...nums) : 0;
}

function feeKr(s: string | undefined): number {
  if (!s) return 0;
  const m = s.replace(/\s/g, "").match(/\d+[,.]?\d*/);
  return m ? Number(m[0].replace(",", ".")) : 0;
}

/** "3 - 92 mån" | "1 - 8 år" -> [min, max] i månader */
function termMonths(termLabel: string): [number, number] {
  const nums = (termLabel.match(/\d+/g) ?? []).map(Number);
  if (!nums.length) return [0, 0];
  const factor = /år/i.test(termLabel) ? 12 : 1;
  const lo = Math.min(...nums) * factor;
  const hi = Math.max(...nums) * factor;
  return [lo, hi];
}

function annuity(amount: number, monthlyRate: number, n: number) {
  if (monthlyRate === 0) return amount / n;
  return (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n));
}

/** Effektiv årsränta (IRR) via bisektion. */
function effectiveRate(net: number, payment: number, n: number): number {
  let lo = 0;
  let hi = 3; // 300 % månadsränta som övre gräns
  const pv = (r: number) => {
    if (r === 0) return payment * n;
    return (payment * (1 - Math.pow(1 + r, -n))) / r;
  };
  if (pv(0) <= net) return 0;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    if (pv(mid) > net) lo = mid;
    else hi = mid;
  }
  const r = (lo + hi) / 2;
  return (Math.pow(1 + r, 12) - 1) * 100;
}

function kr(n: number) {
  return new Intl.NumberFormat("sv-SE").format(Math.round(n)) + " kr";
}

function pct(n: number) {
  return n.toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " %";
}

function computeExample(l: Lender, amount: number) {
  const [lo, hi] = termMonths(l.termLabel);
  const months = Math.min(Math.max(12, lo || 12), hi || 12);
  const nominal = maxRate(l.rate);
  const setup = feeKr(l.setupFee);
  const monthlyFee = feeKr(l.adminFee) + feeKr(l.invoiceFee);
  const payment = annuity(amount, nominal / 100 / 12, months) + monthlyFee;
  const total = payment * months + setup;
  const eff = effectiveRate(amount - setup, payment, months);
  return { months, nominal, setup, monthlyFee, payment, total, eff };
}

export function CostDisclosure({
  page,
  amount,
  className = "",
}: {
  page: PageKey;
  amount?: number;
  className?: string;
}) {
  const list = getListForPage(page, "alla", amount ?? DEFAULT_AMOUNT[page]);
  if (!list.length) return null;


  const terms = list.map((l) => termMonths(l.termLabel)).filter(([lo, hi]) => lo > 0 && hi > 0);
  const minMonths = Math.min(...terms.map(([lo]) => lo));
  const maxMonths = Math.max(...terms.map(([, hi]) => hi));

  const exAmount = 10000;
  const examples = list
    .filter((l) => l.minAmount <= exAmount && l.maxAmount >= exAmount)
    .map((l) => ({ l, ...computeExample(l, exAmount) }));
  const maxApr = 23;
  const ex = examples[0];

  return (
    <section className={`mx-auto max-w-4xl px-4 pt-10 sm:px-6 lg:px-8 ${className}`}>
      <div className="rounded-md border border-border bg-card p-5 sm:p-6">
        <h2 className="font-display text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">
          Kostnadsinformation – privatlån
        </h2>
        <p className="mt-2 text-[13.5px] leading-relaxed text-foreground/80">
          Vi förmedlar endast lån med en återbetalningstid på minst 61 dagar (3 månader). Nedan ser du
          villkorsspannet bland långivarna på den här sidan.
        </p>

        <dl className="mt-4 divide-y divide-border border-y border-border">
          <div className="flex items-start justify-between gap-4 py-2.5">
            <dt className="text-[14px] font-medium text-foreground">Kortaste återbetalningstid</dt>
            <dd className="text-right font-display text-[14px] font-extrabold text-foreground">
              {Math.max(minMonths, 3)} månader
            </dd>
          </div>
          <div className="flex items-start justify-between gap-4 py-2.5">
            <dt className="text-[14px] font-medium text-foreground">Längsta återbetalningstid</dt>
            <dd className="text-right font-display text-[14px] font-extrabold text-foreground">
              {maxMonths} månader
            </dd>
          </div>
          <div className="flex items-start justify-between gap-4 py-2.5">
            <dt className="text-[14px] font-medium text-foreground">
              Högsta effektiva ränta (APR) bland långivarna på denna sida
            </dt>
            <dd className="text-right font-display text-[14px] font-extrabold text-primary">
              {pct(maxApr)}
            </dd>
          </div>
        </dl>

        {ex && (
          <div className="mt-4 rounded-md border border-border bg-background p-4">
            <h3 className="font-display text-[15px] font-extrabold text-foreground">
              Typexempel på total kostnad
            </h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-foreground/85">
              {ex.l.name}: lån på {kr(exAmount)} med {ex.months} månaders löptid och{" "}
              {pct(ex.nominal)} nominell ränta. Uppläggningsavgift {kr(ex.setup)} och avgifter{" "}
              {kr(ex.monthlyFee)}/mån. Månadskostnad {kr(ex.payment)}, total återbetalning{" "}
              {kr(ex.total)} varav {kr(ex.total - exAmount)} i ränta och avgifter. Effektiv ränta{" "}
              {pct(ex.eff)}. Inga andra avgifter tillkommer.
            </p>
          </div>
        )}

      </div>
    </section>
  );
}
